import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Wallet, Swords, Store, ChevronRight, X, Sparkles, Key, Copy, CheckCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

// Generate a simple player ID (8 chars)
const generatePlayerId = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let id = '';
  for (let i = 0; i < 8; i++) {
    id += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return id;
};

// Generate a burner keypair (simplified - just random bytes for demo)
const generateBurnerWallet = () => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  const privateKey = Array.from(array).map(b => b.toString(16).padStart(2, '0')).join('');
  const playerId = generatePlayerId();
  
  return {
    playerId,
    privateKey,
    createdAt: Date.now()
  };
};

const TUTORIAL_STEPS = [
  {
    id: 'wallet-setup',
    title: 'No Wallet? Create One Instantly.',
    description: 'Generate a Player Account to start playing immediately. No downloads needed!',
    icon: Key,
    targetSelector: null, // Full screen step
    position: 'center',
    isWalletCreation: true
  },
  {
    id: 'wallet',
    title: 'Your Credits',
    description: 'Deposit SOL to get game credits. Play games, win prizes!',
    icon: Wallet,
    targetSelector: '[data-testid="prominent-wallet-display"]',
    position: 'bottom'
  },
  {
    id: 'play',
    title: 'Earn Here',
    description: 'Tap PLAY to enter the arena. Slap targets, climb the leaderboard!',
    icon: Swords,
    targetSelector: '[data-testid="nav-play"]',
    position: 'top'
  },
  {
    id: 'market',
    title: 'Trade Here',
    description: 'Buy and sell cards with other players. Build your collection!',
    icon: Store,
    targetSelector: '[data-testid="nav-market"]',
    position: 'top'
  }
];

const OnboardingTutorial = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [burnerWallet, setBurnerWallet] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  const step = TUTORIAL_STEPS[currentStep];
  const isLastStep = currentStep === TUTORIAL_STEPS.length - 1;
  const isWalletStep = step.isWalletCreation;

  // Check if user already has a burner wallet
  useEffect(() => {
    const existingWallet = localStorage.getItem('burner_wallet');
    if (existingWallet) {
      try {
        const wallet = JSON.parse(existingWallet);
        setBurnerWallet(wallet);
      } catch (e) {
        console.error('Failed to parse burner wallet');
      }
    }
  }, []);

  // Find and highlight target element
  useEffect(() => {
    if (!step.targetSelector) {
      setTargetRect(null);
      return;
    }

    const findTarget = () => {
      const target = document.querySelector(step.targetSelector);
      if (target) {
        const rect = target.getBoundingClientRect();
        setTargetRect(rect);
      }
    };

    findTarget();
    window.addEventListener('resize', findTarget);
    return () => window.removeEventListener('resize', findTarget);
  }, [step.targetSelector, currentStep]);

  const handleGenerateWallet = useCallback(async () => {
    setIsGenerating(true);
    
    // Simulate generation delay for UX
    await new Promise(r => setTimeout(r, 1000));
    
    const wallet = generateBurnerWallet();
    localStorage.setItem('burner_wallet', JSON.stringify(wallet));
    localStorage.setItem('player_id', wallet.playerId);
    setBurnerWallet(wallet);
    setIsGenerating(false);
    
    toast.success('Player Account Created!', {
      description: `Your Player ID: ${wallet.playerId}`
    });
  }, []);

  const handleCopyId = useCallback(() => {
    if (burnerWallet?.playerId) {
      navigator.clipboard.writeText(burnerWallet.playerId);
      setCopied(true);
      toast.success('Player ID copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  }, [burnerWallet]);

  const handleNext = () => {
    if (isLastStep) {
      localStorage.setItem('tutorial_completed', 'true');
      onComplete();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleSkip = () => {
    localStorage.setItem('tutorial_completed', 'true');
    onComplete();
  };

  const Icon = step.icon;

  // Wallet Creation Step (Full Screen)
  if (isWalletStep) {
    return (
      <AnimatePresence>
        <motion.div
          className="fixed inset-0 z-[9999] bg-black/95 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Skip Button */}
          <button
            onClick={handleSkip}
            className="absolute top-4 right-4 p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
          >
            <X className="w-5 h-5 text-white/60" />
          </button>

          <motion.div
            className="max-w-md w-full"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.1 }}
          >
            {/* Step indicator */}
            <div className="flex justify-center gap-1.5 mb-6">
              {TUTORIAL_STEPS.map((_, i) => (
                <div
                  key={i}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    i <= currentStep ? 'bg-yellow-400' : 'bg-white/20'
                  }`}
                />
              ))}
            </div>

            <div className="bg-[#0d0d0d] border-2 border-yellow-500/60 rounded-2xl p-6 shadow-2xl shadow-yellow-500/20">
              {/* Icon */}
              <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center">
                <Key className="w-10 h-10 text-white" />
              </div>

              {/* Title */}
              <h2 className="text-2xl font-bold text-white text-center mb-2">
                {step.title}
              </h2>
              <p className="text-white/60 text-center mb-6">
                {step.description}
              </p>

              {/* Wallet Display or Generate Button */}
              {burnerWallet ? (
                <div className="space-y-4">
                  {/* Player ID Display */}
                  <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <CheckCircle className="w-5 h-5 text-green-400" />
                      <span className="text-green-400 font-bold">Account Created!</span>
                    </div>
                    
                    <div className="flex items-center justify-center gap-3 mt-3">
                      <span className="text-white/60 text-sm">Player ID:</span>
                      <code className="bg-black/50 px-3 py-1.5 rounded-lg text-yellow-400 font-mono font-bold text-lg">
                        {burnerWallet.playerId}
                      </code>
                      <button
                        onClick={handleCopyId}
                        className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                      >
                        {copied ? (
                          <CheckCircle className="w-4 h-4 text-green-400" />
                        ) : (
                          <Copy className="w-4 h-4 text-white/60" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Warning */}
                  <div className="flex items-start gap-2 text-xs text-white/40">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-yellow-500" />
                    <span>This is a temporary account stored in your browser. Save your secret key safely!</span>
                  </div>

                  {/* Continue Button */}
                  <button
                    onClick={handleNext}
                    className="w-full py-4 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-xl font-bold text-black hover:from-yellow-400 hover:to-orange-400 transition-colors flex items-center justify-center gap-2"
                  >
                    CONTINUE
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Generate Button */}
                  <button
                    onClick={handleGenerateWallet}
                    disabled={isGenerating}
                    className="w-full py-4 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl font-bold text-white hover:from-green-400 hover:to-emerald-400 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {isGenerating ? (
                      <>
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                        >
                          <Sparkles className="w-5 h-5" />
                        </motion.div>
                        GENERATING...
                      </>
                    ) : (
                      <>
                        <Key className="w-5 h-5" />
                        GENERATE PLAYER ACCOUNT
                      </>
                    )}
                  </button>

                  {/* Alternative: Skip */}
                  <button
                    onClick={handleNext}
                    className="w-full py-3 text-white/40 text-sm hover:text-white/60 transition-colors"
                  >
                    Skip for now →
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  }

  // Standard Tutorial Step with Spotlight
  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[9999]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        {/* Dark overlay with cutout for target */}
        <svg className="absolute inset-0 w-full h-full">
          <defs>
            <mask id="spotlight">
              <rect x="0" y="0" width="100%" height="100%" fill="white" />
              {targetRect && (
                <rect
                  x={targetRect.left - 8}
                  y={targetRect.top - 8}
                  width={targetRect.width + 16}
                  height={targetRect.height + 16}
                  rx="12"
                  fill="black"
                />
              )}
            </mask>
          </defs>
          <rect
            x="0"
            y="0"
            width="100%"
            height="100%"
            fill="rgba(0,0,0,0.85)"
            mask="url(#spotlight)"
          />
        </svg>

        {/* Highlight border around target */}
        {targetRect && (
          <motion.div
            className="absolute border-2 border-yellow-400 rounded-xl pointer-events-none"
            style={{
              left: targetRect.left - 8,
              top: targetRect.top - 8,
              width: targetRect.width + 16,
              height: targetRect.height + 16,
            }}
            animate={{
              boxShadow: [
                '0 0 20px rgba(250,204,21,0.5)',
                '0 0 40px rgba(250,204,21,0.8)',
                '0 0 20px rgba(250,204,21,0.5)',
              ]
            }}
            transition={{ duration: 1.5, repeat: Infinity }}
          />
        )}

        {/* Tooltip */}
        {targetRect && (
          <motion.div
            className={`absolute left-4 right-4 max-w-sm mx-auto ${
              step.position === 'top' 
                ? 'bottom-auto' 
                : 'top-auto'
            }`}
            style={{
              [step.position === 'top' ? 'bottom' : 'top']: 
                step.position === 'top' 
                  ? `${window.innerHeight - targetRect.top + 20}px`
                  : `${targetRect.bottom + 20}px`
            }}
            initial={{ opacity: 0, y: step.position === 'top' ? 20 : -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="bg-[#0d0d0d] border-2 border-yellow-500/60 rounded-2xl p-5 shadow-2xl shadow-yellow-500/20">
              {/* Step indicator */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex gap-1.5">
                  {TUTORIAL_STEPS.map((_, i) => (
                    <div
                      key={i}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        i <= currentStep ? 'bg-yellow-400' : 'bg-white/20'
                      }`}
                    />
                  ))}
                </div>
                <button
                  onClick={handleSkip}
                  className="text-white/40 text-xs hover:text-white/60 transition-colors"
                >
                  Skip Tutorial
                </button>
              </div>

              {/* Content */}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-white mb-1">{step.title}</h3>
                  <p className="text-white/70 text-sm">{step.description}</p>
                </div>
              </div>

              {/* Action Button */}
              <button
                onClick={handleNext}
                className="w-full mt-4 py-3 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-xl font-bold text-black hover:from-yellow-400 hover:to-orange-400 transition-colors flex items-center justify-center gap-2"
              >
                {isLastStep ? (
                  <>
                    <Sparkles className="w-5 h-5" />
                    START PLAYING
                  </>
                ) : (
                  <>
                    NEXT
                    <ChevronRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </div>
          </motion.div>
        )}

        {/* Skip Button (floating) */}
        <button
          onClick={handleSkip}
          className="absolute top-4 right-4 p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
        >
          <X className="w-5 h-5 text-white/60" />
        </button>
      </motion.div>
    </AnimatePresence>
  );
};

export default OnboardingTutorial;
